import { Component } from '@theme/component';
import { isDesktopBreakpoint } from '@theme/utilities';

/**
 * A custom element that handles image zoom in the dimension section.
 * On desktop, it allows for a hover-style zoom on click/move.
 * On mobile, it opens a global zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} image - The gallery image to zoom.
 * @property {HTMLElement} wrapper - The wrapper element for the image.
 *
 * @extends Component<Refs>
 */
export class PreviewImageZoom extends Component {
    static requiredRefs = ['image'];

    #zoomed = false;
    #scale = 1.5;
    #ticking = false;
    #boundMouseMove;
    #boundMouseLeave;

    connectedCallback() {
        super.connectedCallback();
        this.#scale = parseFloat(this.dataset.scale) || 1.5;

        // Manual listener for mousemove since it's not handled by the declarative system in Component.js
        this.#boundMouseMove = this.handleMouseMove.bind(this);
        this.#boundMouseLeave = this.handleMouseLeave.bind(this);

        this.addEventListener('mousemove', this.#boundMouseMove);
        this.addEventListener('mouseleave', this.#boundMouseLeave);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('mousemove', this.#boundMouseMove);
        this.removeEventListener('mouseleave', this.#boundMouseLeave);
    }

    handleMouseLeave() {
        if (this.#zoomed) {
            this.toggleZoom();
        }
    }

    /**
     * Handles click events to toggle zoom on desktop or open dialog on mobile.
     * @param {PointerEvent} event
     */
    handleClick(event) {
        if (!isDesktopBreakpoint()) {
            this.openZoomDialog(event);
            return;
        }

        let x = 50;
        let y = 50;

        if ((event.clientX !== 0 || event.clientY !== 0) && this.offsetWidth && this.offsetHeight) {
            const rect = this.getBoundingClientRect();
            x = ((event.clientX - rect.left) / this.offsetWidth) * 100;
            y = ((event.clientY - rect.top) / this.offsetHeight) * 100;
        }

        this.toggleZoom(x, y);
    }

    /**
     * Handles mouse move to pan the zoomed image on desktop.
     * @param {MouseEvent} event
     */
    handleMouseMove(event) {
        if (!this.#zoomed || !isDesktopBreakpoint() || this.#ticking) return;

        this.#ticking = true;
        window.requestAnimationFrame(() => {
            const { image } = this.refs;
            const rect = this.getBoundingClientRect();
            const x = ((event.clientX - rect.left) / this.offsetWidth) * 100;
            const y = ((event.clientY - rect.top) / this.offsetHeight) * 100;

            image.style.transformOrigin = `${x}% ${y}%`;
            this.#ticking = false;
        });
    }

    /**
     * Accessibility handler for keyboard interaction.
     * @param {KeyboardEvent} event
     */
    handleKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            if (isDesktopBreakpoint()) {
                this.toggleZoom(50, 50);
            } else {
                this.openZoomDialog(event);
            }
        }
    }

    /**
     * Toggles the zoom state on desktop.
     * @param {number} xPercent
     * @param {number} yPercent
     */
    toggleZoom(xPercent = 50, yPercent = 50) {
        const { image } = this.refs;

        if (!this.#zoomed) {
            image.style.transform = `scale(${this.#scale})`;
            image.style.transition = 'transform 0.3s ease';
            image.style.transformOrigin = `${xPercent}% ${yPercent}%`;
            this.#zoomed = true;
            this.classList.add('zoom-active');

            const slide = this.closest('slideshow-slide');
            if (slide) {
                slide.classList.add('zoom-image-active');
            }
        } else {
            image.style.transform = 'scale(1)';
            image.style.transformOrigin = 'center center';
            this.#zoomed = false;
            this.classList.remove('zoom-active');

            const slide = this.closest('slideshow-slide');
            if (slide) {
                slide.classList.remove('zoom-image-active');
            }
        }
    }

    /**
     * Opens the global mobile zoom dialog.
     * @param {Event} event
     */
    openZoomDialog(event) {
        const index = parseInt(this.dataset.index) || 0;
        // Audit note: intentional cross-component lookup (do not flag) — the shared zoom dialog
        // lives outside this component's subtree; scoped to the nearest section first, with a
        // document fallback.
        const section = this.closest('.shopify-section');
        const zoomDialog = (section || document).querySelector('zoom-dialog');

        if (zoomDialog && typeof zoomDialog.open === 'function') {
            zoomDialog.open(index, event);
        }
    }
}

if (!customElements.get('preview-image-zoom-component')) {
    customElements.define('preview-image-zoom-component', PreviewImageZoom);
}
